import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Receipt } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getDocs, collection, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '@/firebase';

interface Bill {
  id: string;
  name: string;
  amount: number;
  frequency: 'monthly' | 'quarterly' | 'annual' | 'one-time';
  nextDueDate: string;
  isActive: boolean;
}

export const BillsWidget: React.FC<{ householdId: string | null }> = ({ householdId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!householdId) return;

    const fetchBills = async () => {
      try {
        const billsRef = collection(db, 'households', householdId, 'bills');
        const billsQuery = query(
          billsRef,
          where('isActive', '==', true),
          orderBy('nextDueDate'),
          limit(20)
        );
        const snapshot = await getDocs(billsQuery);
        const billsData: Bill[] = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Bill[];
        setBills(billsData);
      } catch (error) {
        console.error('Error fetching bills:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBills();
  }, [householdId]);

  if (!householdId) return null;

  const normalizeAmount = (amount: number, frequency: Bill['frequency']): number => {
    switch (frequency) {
      case 'monthly': return amount;
      case 'quarterly': return amount / 3;
      case 'annual': return amount / 12;
      case 'one-time': return 0;
      default: return amount;
    }
  };

  const getDaysUntilDue = (dueDate: string): number => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const diffTime = due.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const activeCount = bills.filter((b) => b.isActive).length;
  const monthlyTotal = bills.reduce((sum, bill) => sum + normalizeAmount(bill.amount, bill.frequency), 0);

  const nextDueBill = bills.find((bill) => {
    const days = getDaysUntilDue(bill.nextDueDate);
    return days >= 0;
  });

  const daysUntilNext = nextDueBill ? getDaysUntilDue(nextDueBill.nextDueDate) : null;

  const getBadgeColor = (days: number | null): string => {
    if (days === null) return 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400';
    if (days < 3) return 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400';
    if (days < 7) return 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400';
    return 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400';
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg p-4 w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Receipt className="w-4 h-4 text-blue-400" />
          <span className="text-slate-900 dark:text-white font-medium text-sm">{t('bills.widget_title', 'Bills')}</span>
        </div>
        <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs px-2 py-0.5 rounded-full">
          {activeCount} {t('bills.widget_active', 'active')}
        </span>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="space-y-2">
          <div className="animate-pulse bg-slate-200 dark:bg-slate-700 rounded h-4 w-3/4" />
          <div className="animate-pulse bg-slate-200 dark:bg-slate-700 rounded h-4 w-1/2" />
          <div className="animate-pulse bg-slate-200 dark:bg-slate-700 rounded h-4 w-5/6" />
        </div>
      )}

      {/* Empty State */}
      {!loading && bills.length === 0 && (
        <div className="text-center py-4">
          <p className="text-slate-500 dark:text-slate-400 text-sm mb-3">{t('bills.widget_empty', 'No bills tracked')}</p>
          <button
            onClick={() => navigate('/finance')}
            className="text-blue-400 text-sm hover:text-blue-300 transition-colors"
          >
            {t('bills.widget_add', 'Add bills')} →
          </button>
        </div>
      )}

      {/* Bills Content */}
      {!loading && bills.length > 0 && (
        <>
          {/* Monthly Total */}
          <div className="mb-3">
            <span className="text-2xl font-bold text-slate-900 dark:text-white">
              ${monthlyTotal.toFixed(2)}
            </span>
            <span className="text-slate-500 dark:text-slate-400 text-sm ml-1">/mo</span>
          </div>

          {/* Next Due Row */}
          {nextDueBill && daysUntilNext !== null && (
            <div className="flex items-center justify-between mb-3">
              <span className="text-slate-700 dark:text-slate-300 text-sm truncate mr-2">{nextDueBill.name}</span>
              <span className={`text-xs px-2 py-0.5 rounded flex-shrink-0 ${getBadgeColor(daysUntilNext)}`}>
                {daysUntilNext === 0 ? 'Today' : daysUntilNext === 1 ? 'Tomorrow' : `${daysUntilNext}d`}
              </span>
            </div>
          )}
        </>
      )}

      {/* Footer */}
      <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
        <button
          onClick={() => navigate('/finance')}
          className="text-blue-400 text-xs hover:text-blue-300 transition-colors"
        >
          {t('bills.widget_manage', 'Manage Bills')} →
        </button>
      </div>
    </div>
  );
};